import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Box,
  Alert,
  Button,
  Chip,
  Divider,
  Stack
} from '@mui/material';
import { Notification as NotificationIcon, NotificationBing, Setting2, Send } from 'iconsax-react';
import useFCM from 'hooks/useFCM';

const NotificationSettings: React.FC = () => {
  const { hasPermission, isSupported, isLoading, requestPermission, token } = useFCM();
  const [isEnabling, setIsEnabling] = useState(false);
  const [isSendingDemo, setIsSendingDemo] = useState(false);
  const handleToggleNotifications = async () => {
    if (hasPermission) {
      // For disabling, we'll just show browser settings info
      alert('To disable notifications, please go to your browser settings:\n\n' +
            'Chrome: Settings > Privacy and Security > Site Settings > Notifications\n' +
            'Firefox: Settings > Privacy & Security > Permissions > Notifications\n' +
            'Safari: Safari > Settings > Websites > Notifications');
      return;
    }

    try {
      setIsEnabling(true);
      console.log('NotificationSettings: Starting permission request...');
      await requestPermission();
      console.log('NotificationSettings: Permission request completed successfully');
    } catch (error) {
      console.error('NotificationSettings: Failed to enable notifications:', error);
    } finally {
      setIsEnabling(false);
    }
  };

  const sendDemoNotification = async () => {
    if (!hasPermission) {
      alert('Please enable notifications first to test demo notification.');
      return;
    }

    // Double-check browser permission
    if (!('Notification' in window)) {
      alert('This browser does not support notifications.');
      return;
    }

    if (Notification.permission !== 'granted') {
      alert('Notification permission is not granted. Please enable notifications first.');
      return;
    }

    try {
      setIsSendingDemo(true);
      console.log('Sending demo notification...');
      
      // Method 1: Try to simulate FCM notification through service worker
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        if (registration) {
          console.log('Found service worker, showing notification via service worker...');
          
          // Show notification through service worker (similar to FCM background notifications)
          await registration.showNotification('🎉 Test Push Notification', {
            body: 'This is a push notification from your app! This is how notification will look.',
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: 'demo-fcm-notification',
            requireInteraction: false,
            silent: false,
            data: {
              type: 'demo',
              timestamp: new Date().toISOString(),
              url: window.location.origin
            }
          });
          
          console.log('Service worker notification sent successfully');
        }
      }

      console.log('Demo notification process completed');
      
    } catch (error) {
      console.error('Failed to send demo notification:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to send demo notification: ${errorMessage}\n\nThis could be due to:\n- Browser focus mode\n- Do Not Disturb settings\n- Browser notification settings`);
    } finally {
      setIsSendingDemo(false);
    }
  };

  const clearDismissedStatus = () => {
    localStorage.removeItem('fcm-permission-dismissed');
    window.location.reload();
  };

  if (!isSupported) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <NotificationBing size={24} color="#f44336" />
            <Typography variant="h6">Push Notifications</Typography>
          </Box>
          <Alert severity="warning">
            Push notifications are not supported by your browser. Please use a modern browser like Chrome, Firefox, or Safari.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <NotificationBing size={24} color={hasPermission ? "#4caf50" : "#ff9800"} />
          <Typography variant="h6">Push Notifications</Typography>
          <Chip 
            label={hasPermission ? "Enabled" : "Disabled"} 
            color={hasPermission ? "success" : "default"}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Receive real-time notifications for important updates, messages, and system alerts.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={hasPermission}
              onChange={handleToggleNotifications}
              disabled={isLoading || isEnabling}
              sx={{
                            // '& .MuiSwitch-thumb': {
                            //   backgroundColor: theme.palette.secondary.dark
                            // },
                            // '& .Mui-checked .MuiSwitch-thumb': {
                            //   backgroundColor: theme.palette.secondary.dark
                            // },
                            '& .MuiSwitch-track': {
                              backgroundColor: "secondary.dark"
                            },
                            '& .Mui-checked + .MuiSwitch-track': {
                              backgroundColor: "secondary.dark"
                            }
                          }}

            />
          }
          label={`Push Notifications ${hasPermission ? 'Enabled' : 'Disabled'}`}
        />

        {hasPermission && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Notifications are active!</strong> You'll receive real-time alerts for important updates.
              </Typography>
            </Alert>
            
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Button
              sx={{color:"text.main",
                bgcolor:"secondary.dark",'&:hover': {
                    bgcolor: 'secondary.main'
                  },}}
                variant="outlined"
                onClick={sendDemoNotification}
                disabled={isSendingDemo}
                startIcon={<Send size={18} />}
                size="small"
              >
                {isSendingDemo ? 'Sending...' : 'Test Notification'}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Send a demo notification to see how it appears in your system notifications.
            </Typography>
            
            {token && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Device Registration Status
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                  Token: {token.substring(0, 20)}...
                </Typography>
              </Box>
            )}
          </>
        )}

        {!hasPermission && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Enable notifications to stay updated with real-time alerts and never miss important information.
              </Typography>
            </Alert>

            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                sx={{color:"text.main",
                bgcolor:"secondary.dark",'&:hover': {
                    bgcolor: 'secondary.main'
                  },}}
                onClick={handleToggleNotifications}
                disabled={isLoading || isEnabling}
                startIcon={<NotificationIcon size={18} />}
              >
                {isEnabling ? 'Enabling...' : 'Enable Notifications'}
              </Button>

              {localStorage.getItem('fcm-permission-dismissed') && (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{color:"text.main",
                bgcolor:"secondary.dark",'&:hover': {
                    bgcolor: 'secondary.main'
                  },}}
                  onClick={clearDismissedStatus}
                  startIcon={<Setting2 size={16} />}
                >
                  Reset Dialog
                </Button>
              )}
            </Stack>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          <strong>Note:</strong> You can manage notification preferences anytime in your browser settings. 
          Notifications help you stay informed about important updates and system alerts.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
